import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { brandOr } from '../../utils/brand';
import { pageToolbar, PAGE_INSET } from '../../tabPalette';   // the page toolbar's bubble + the one page gutter (report 76a2c198)

// Compliance / filing-deadline calendar. Lists every accounting client's tax-filing deadlines
// by month, with status (pending / filed / N-A). Reminders fire 7/3/1 days before via email + TG.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ddl: React.CSSProperties = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' };
// 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
// Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this:
// the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular weight, where
// they were dark grey in bold, 10px above and below on one 16px line, a 37px band, white where it was light grey.
// 10 → 5px under the heading text (report c151d269: "Bring the heading text 5 pixels closer to the grey line
// below. Do this for every page." — see Active Jobs' th: same top padding, sides, size, colour and line).
const th: React.CSSProperties = { fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px 5px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
const td: React.CSSProperties = { fontSize: 12.5, padding: '7px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };

type Row = { id: string; client_id: string; kind: string; period: string; due_date: string; status: string; company_name: string; client_name: string; note?: string };

const fmtDate = (ymd: string) => { const p = String(ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : ymd; };
const todayStr = () => new Date().toISOString().slice(0, 10);

function StatusChip({ status, due, onSet }: { status: string; due: string; onSet: (s: string) => void }) {
  const overdue = status === 'pending' && due < todayStr();
  const map: Record<string, { bg: string; c: string; label: string }> = {
    pending: overdue ? { bg: '#FEF2F2', c: '#DC2626', label: 'Overdue' } : { bg: '#FFFBEB', c: '#B45309', label: 'Pending' },
    filed: { bg: '#ECFDF5', c: '#15803D', label: 'Filed' },
    na: { bg: 'var(--c-gray-100)', c: 'var(--text-tertiary)', label: 'N/A' },
  };
  const s = map[status] || map.pending;
  return (
    <select value={status} onChange={e => onSet(e.target.value)} style={{ ...ddl, padding: '4px 8px', fontSize: 11.5, fontWeight: 700, background: s.bg, color: s.c, border: `1px solid ${s.c}33` }}>
      <option value="pending">{overdue ? 'Overdue' : 'Pending'}</option>
      <option value="filed">Filed ✓</option>
      <option value="na">N/A</option>
    </select>
  );
}

export default function Compliance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [rows, setRows] = useState<Row[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [genBusy, setGenBusy] = useState(false);

  const generateAll = async () => {
    if (genBusy) return;
    if (!window.confirm(t('cmp_genall_confirm'))) return;
    setGenBusy(true);
    try { const r: any = await api.compGenerateAll(); window.alert(t('cmp_genall_done', { clients: r.clients, created: r.created })); load(); }
    catch (e: any) { window.alert(e.message || 'failed'); }
    finally { setGenBusy(false); }
  };

  useEffect(() => { api.compLabels().then((d: any) => setLabels(d || {})).catch(() => {}); }, []);
  const load = () => { setLoading(true); api.compList(month, { status }).then((d: any) => { setRows(d.rows || []); setCounts(d.counts || {}); }).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [month, status]); // eslint-disable-line

  const setRowStatus = async (r: Row, s: string) => {
    setRows(p => p.map(x => x.id === r.id ? { ...x, status: s } : x));
    try { await api.compUpdate(r.id, { status: s }); } catch {}
  };

  const shiftMonth = (k: number) => { const [y, m] = month.split('-').map(Number); const d = new Date(y, m - 1 + k, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); };

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter(r => !ql || (r.company_name || '').toLowerCase().includes(ql) || (r.client_name || '').toLowerCase().includes(ql));
  }, [rows, q]);

  const [yy, mm] = month.split('-').map(Number);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_compliance')}</div>
      </header>

      {/* The page's toolbar — the month, the status chips, search and Generate — is a bubble, as every page toolbar is
          (Calem, report 76a2c198, 2nd opening — see pageToolbar): 10px corners and a --border-strong line all the way
          round where a --border line ran under it alone, PAGE_INSET in from the page's edges — the one page gutter the
          whole system stands in by, 15px, where this page's own was 18 (Calem, report 76a2c198, 5th opening: "Move the
          left and right page spacing in from the edges to 15 px on every page of the SLF system"; see PAGE_INSET in
          tabPalette) — with a gap above and below it. The list below it takes the same 15, so its box's ends still
          stand under the bubble's. Its white fill and everything on it are as they were; the logo header above keeps
          its full-width line. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', ...pageToolbar(), background: 'var(--bg-surface)', flexWrap: 'wrap' }}>
        <button onClick={() => shiftMonth(-1)} style={{ ...ddl, padding: '6px 10px' }}><i className="ti ti-chevron-left" /></button>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', minWidth: 120, textAlign: 'center' }}>{MONTHS[mm - 1]} {yy}</div>
        <button onClick={() => shiftMonth(1)} style={{ ...ddl, padding: '6px 10px' }}><i className="ti ti-chevron-right" /></button>
        <div style={{ flex: 1 }} />
        {/* status chips */}
        {[['', t('cmp_all')], ['pending', t('cmp_pending')], ['filed', t('cmp_filed')]].map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} style={{ ...ddl, fontWeight: 700, background: status === v ? 'var(--accent)' : 'var(--bg-surface)', color: status === v ? '#fff' : 'var(--text-secondary)', border: status === v ? 'none' : '1px solid var(--border-strong)' }}>
            {l}{v === 'pending' && counts.pending ? ` (${counts.pending})` : ''}{v === 'filed' && counts.filed ? ` (${counts.filed})` : ''}
          </button>
        ))}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('cmp_search')} style={{ ...ddl, fontWeight: 400, width: 200, cursor: 'text' }} />
        <button onClick={generateAll} disabled={genBusy} title={t('cmp_genall_hint')} style={{ ...ddl, fontWeight: 700, background: genBusy ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
          <i className="ti ti-calendar-plus" style={{ display: 'inline-block', animation: genBusy ? 'spin 0.8s linear infinite' : undefined }} /> {t('cmp_genall')}</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: `16px ${PAGE_INSET}px`, minHeight: 0 }}>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
          : filtered.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('cmp_none')}</div>
            : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>{t('cmp_due')}</th><th style={th}>{t('cmp_client')}</th><th style={th}>{t('cmp_filing')}</th>
                    <th style={th}>{t('cmp_period')}</th><th style={{ ...th, width: 140 }}>{t('cmp_status')}</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(r => {
                      const overdue = r.status === 'pending' && r.due_date < todayStr();
                      return (
                        <tr key={r.id} style={{ background: overdue ? '#FEF2F2' : undefined }}>
                          <td style={{ ...td, fontWeight: 700, color: overdue ? '#DC2626' : 'var(--text-primary)', whiteSpace: 'nowrap' }}>{fmtDate(r.due_date)}</td>
                          <td style={td}>{r.company_name || r.client_name || '—'}</td>
                          <td style={td}>{labels[r.kind] || r.kind}</td>
                          <td style={{ ...td, color: 'var(--text-tertiary)' }}>{r.period}</td>
                          <td style={td}><StatusChip status={r.status} due={r.due_date} onSet={s => setRowStatus(r, s)} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 12 }}>{t('cmp_hint')}</div>
      </div>
    </div>
  );
}
